import { useCallback } from "react";
import { Link, useNavigate, type LinkProps } from "react-router-dom";

import { buildMrpSkuDetailPath, type MrpSkuDetailTab } from "@/lib/mrp";
import { cn } from "@/lib/utils";

type MrpSkuDetailLinkProps = Omit<LinkProps, "to"> & {
  sku: string;
  tab?: MrpSkuDetailTab;
};

/**
 * Renders a client-side link to `/mrp/skus/:sku` with correct encoding and optional `?tab=`.
 * Prefer this over hand-built paths anywhere in the app (MRP, BoM, or ERP).
 */
export function MrpSkuDetailLink({ sku, tab, className, children, ...rest }: MrpSkuDetailLinkProps) {
  return (
    <Link
      to={buildMrpSkuDetailPath(sku, tab != null ? { tab } : undefined)}
      className={cn(
        "font-medium text-sky-700 hover:text-sky-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1",
        className,
      )}
      {...rest}
    >
      {children ?? sku}
    </Link>
  );
}

/**
 * Returns a `navigate` function that goes to the full-page MRP SKU detail route.
 * Use for buttons, row actions, or post-mutation navigation from any page.
 */
export function useNavigateToMrpSkuDetail(): (
  sku: string,
  options?: { tab?: MrpSkuDetailTab; replace?: boolean; state?: unknown },
) => void {
  const navigate = useNavigate();
  return useCallback(
    (sku, options) => {
      const tab = options?.tab;
      navigate(buildMrpSkuDetailPath(sku, tab != null ? { tab } : undefined), {
        replace: options?.replace,
        state: options?.state,
      });
    },
    [navigate],
  );
}
